import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaMoneyBillWave,
  FaListUl,
  FaPrint,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const FeesStructure = () => {
  const navigate = useNavigate();
  const [feeStructures, setFeeStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    class_id: '',
    tuition_fee: '',
    admission_fee: '',
    course_start_date: '',
    course_end_date: '',
  });
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const { selectedAcademicYear } = useAppContext();

  useEffect(() => {
    fetchClasses();
    fetchFeeStructures();
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  const fetchClasses = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-classes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.status === 'success') {
        setClasses(response.data.data || []);
      } else {
        showAlert('Failed to fetch classes', 'danger');
      }
    } catch (error) {
      console.error('Failed to fetch classes', error);
      showAlert('Error fetching classes', 'danger');
    }
  };

  const fetchFeeStructures = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    if (!selectedAcademicYear?.id) {
      showAlert('Please select an academic year.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_STRUCTURE.LIST}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      if (response.data.status === 'success') {
        setFeeStructures(response.data.data || []);
      } else {
        showAlert('Failed to fetch fee structures', 'danger');
      }
    } catch (error) {
      console.error('Failed to fetch fee structures', error);
      showAlert('Error fetching fee structures', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    if (!selectedAcademicYear?.id) {
      showAlert('Please select an academic year.', 'warning');
      return;
    }

    if (
      !formData.name ||
      !formData.class_id ||
      !formData.tuition_fee ||
      !formData.admission_fee ||
      !formData.course_start_date ||
      !formData.course_end_date
    ) {
      showAlert('Please fill in all required fields', 'warning');
      return;
    }

    setSaving(true);
    try {
      const endpoint = formData.id
        ? appConstants.FEE_STRUCTURE.UPDATE
        : appConstants.FEE_STRUCTURE.CREATE;

      const payload = {
        name: formData.name,
        class_id: parseInt(formData.class_id),
        tuition_fee: parseFloat(formData.tuition_fee),
        admission_fee: parseFloat(formData.admission_fee),
        course_start_date: formData.course_start_date,
        course_end_date: formData.course_end_date,
        academic_year_id: selectedAcademicYear.id,
      };

      if (formData.id) {
        payload.id = formData.id;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert(
          `Fee structure ${formData.id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setShowModal(false);
        resetForm();
        fetchFeeStructures();
      } else {
        showAlert(
          response.data.message || 'Failed to save fee structure',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error saving fee structure:', error);
      showAlert('Error saving fee structure', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (feeStructure) => {
    // Find the class_id by matching class_name from the API response
    const matchingClass = classes.find(
      (cls) => cls.name === feeStructure.class_name,
    );

    setFormData({
      id: feeStructure.id,
      name: feeStructure.name || '',
      class_id: matchingClass ? matchingClass.id : '',
      tuition_fee: feeStructure.tuition_fee || '',
      admission_fee: feeStructure.admission_fee || '',
      course_start_date: feeStructure.course_start_date || '',
      course_end_date: feeStructure.course_end_date || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm('Are you sure you want to delete this fee structure?')
    ) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_STRUCTURE.DELETE}`,
        { id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert('Fee structure deleted successfully!', 'success');
        fetchFeeStructures();
      } else {
        showAlert(
          response.data.message || 'Failed to delete fee structure',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error deleting fee structure:', error);
      showAlert('Error deleting fee structure', 'danger');
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      class_id: '',
      tuition_fee: '',
      admission_fee: '',
      course_start_date: '',
      course_end_date: '',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const filteredFeeStructures = feeStructures.filter((feeStructure) => {
    return (
      (!search ||
        feeStructure.name?.toLowerCase().includes(search.toLowerCase())) &&
      (!classFilter ||
        feeStructure.class_name
          ?.toLowerCase()
          .includes(classFilter.toLowerCase()))
    );
  });

  return (
    <Layout>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>
            <FaMoneyBillWave className="me-2" />
            Fees Structure Management
          </h4>
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            style={{ backgroundColor: '#0657CE' }}
          >
            <FaPlus className="me-2" />
            Add Fee Structure
          </Button>
        </div>

        {/* Floating Alert (bottom left) */}
        {alert.show && (
          <div
            style={{
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '6px',
                background:
                  alert.variant === 'success'
                    ? '#e6f7e6'
                    : alert.variant === 'danger'
                      ? '#ffeaea'
                      : '#fff8e1',
                color: '#5c4200',
                marginBottom: 0,
                fontWeight: 500,
                fontSize: '0.92rem',
                padding: '6px 32px 6px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '320px',
              }}
            >
              <span style={{ flex: 1 }}>{alert.message}</span>
              <button
                onClick={() =>
                  setAlert({ show: false, message: '', variant: '' })
                }
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '8px',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#5c4200',
                  fontWeight: 700,
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-6">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search fee structures..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <Form.Control
              type="text"
              placeholder="Filter by class..."
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setSearch('');
                setClassFilter('');
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Fee Structures Table */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead style={{ backgroundColor: '#0657CE', color: 'white' }}>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>Tuition Fee</th>
                <th>Admission Fee</th>
                <th>Course Period</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeeStructures.length > 0 ? (
                filteredFeeStructures.map((feeStructure) => (
                  <tr key={feeStructure.id}>
                    <td>{feeStructure.name}</td>
                    <td>{feeStructure.class_name}</td>
                    <td>
                      ₹
                      {parseFloat(
                        feeStructure.tuition_fee || 0,
                      ).toLocaleString()}
                    </td>
                    <td>
                      ₹
                      {parseFloat(
                        feeStructure.admission_fee || 0,
                      ).toLocaleString()}
                    </td>
                    <td>
                      {feeStructure.course_start_date} to{' '}
                      {feeStructure.course_end_date}
                    </td>
                    <td>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-2"
                        onClick={() =>
                          navigate(`/fee-structure-view/${feeStructure.id}`)
                        }
                        title="View/Print Fee Structure"
                      >
                        <FaPrint />
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-2"
                        onClick={() =>
                          navigate(`/fee-slabs/${feeStructure.id}`)
                        }
                        title="Manage Fee Slabs"
                      >
                        <FaListUl />
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(feeStructure)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(feeStructure.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No fee structures found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? 'Edit Fee Structure' : 'Add New Fee Structure'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter fee structure name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Class *</Form.Label>
                  <Form.Select
                    value={formData.class_id}
                    onChange={(e) =>
                      setFormData({ ...formData, class_id: e.target.value })
                    }
                    disabled={!!formData.id}
                  >
                    <option value="">Select Class</option>
                    {classes.map((classItem) => (
                      <option key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Tuition Fee *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter tuition fee amount"
                    value={formData.tuition_fee}
                    onChange={(e) =>
                      setFormData({ ...formData, tuition_fee: e.target.value })
                    }
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Admission Fee *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter admission fee amount"
                    value={formData.admission_fee}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        admission_fee: e.target.value,
                      })
                    }
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Course Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.course_start_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        course_start_date: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Course End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.course_end_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        course_end_date: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#0657CE' }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>{formData.id ? 'Update' : 'Create'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default FeesStructure;
